import { Box, Button, Typography } from '@mui/material';
import { Mail, OpenInNew } from '@mui/icons-material';
import { useQuery } from '@apollo/client/react';
import { gql } from '../../__generated__';
import { useConfig } from '../../components/Configuration';
import { formatDate } from '../../Formatters';
import { tokens } from './tokens';
import LoadingSpinner from '../../components/LoadingSpinner';
import { SALG_API_URL } from '../../config';

interface Notification {
  id: string;
  subject: string;
  sentAt: string;
}

const USER_NOTIFICATIONS = gql(`
  query UserNotificationsList($userId: Int!, $offset: Int, $limit: Int) {
    queryResult: getUserNotifications(userId: $userId, offset: $offset, limit: $limit) {
      totalCount
      notifications {
        id
        subject
        sentAt
      }
    }
  }
`);

interface Props {
  userId: number;
}

export function NotificationsPanel({ userId }: Props) {
  const config = useConfig();
  const pollInterval = config?.defaultPollingInterval ?? 0;

  const { loading, error, data, fetchMore } = useQuery<{
    queryResult: { totalCount: number; notifications: Notification[] };
  }>(USER_NOTIFICATIONS, {
    variables: { userId, offset: 0, limit: 5 },
    pollInterval,
  });

  if (error) return <Box sx={{ p: 3 }}><span>Feil! {error.message}</span></Box>;
  if (loading && !data) return <Box sx={{ p: 3 }}><LoadingSpinner /></Box>;

  const list = data?.queryResult.notifications ?? [];
  const moreToLoad = data ? list.length < data.queryResult.totalCount : false;

  if (list.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="caption" color="textDisabled">Ingen e-poster</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {list.map((n, i) => (
        <Box
          key={n.id}
          component="a"
          href={`${SALG_API_URL}/notification/user/${userId}?notificationId=${n.id}`}
          target="_blank"
          rel="noreferrer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.75,
            padding: { xs: '12px 16px', sm: '14px 24px' },
            borderTop: i === 0 ? 'none' : `1px solid ${tokens.divider}`,
            cursor: 'pointer',
            color: tokens.text,
            textDecoration: 'none',
            transition: 'background 120ms ease',
            '&:hover': { background: tokens.surfaceAlt },
          }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '6px',
              background: tokens.primaryTint,
              color: tokens.primaryDark,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
            <Mail sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 14,
                color: tokens.text,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              dangerouslySetInnerHTML={{ __html: n.subject }}
            />
            <Typography sx={{ fontSize: 12, color: tokens.textSecondary, mt: 0.25 }}>{formatDate(n.sentAt)}</Typography>
          </Box>
          <OpenInNew sx={{ fontSize: 14, color: tokens.textDisabled }} />
        </Box>
      ))}

      {moreToLoad && (
        <Box sx={{ padding: { xs: '10px 16px', sm: '12px 24px' }, textAlign: 'center', borderTop: `1px solid ${tokens.divider}` }}>
          <Button
            size="small"
            sx={{ textTransform: 'none' }}
            disabled={loading}
            onClick={() =>
              fetchMore({
                variables: { offset: list.length },
                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult?.queryResult?.notifications?.length) return prev;
                  return {
                    ...prev,
                    queryResult: {
                      ...prev.queryResult,
                      notifications: [...prev.queryResult.notifications, ...fetchMoreResult.queryResult.notifications],
                    },
                  };
                },
              })
            }>
            Last flere
          </Button>
        </Box>
      )}
    </Box>
  );
}
